package one.healthium.pcos.presentation.navigation.main

import co.touchlab.kermit.Logger
import com.arkivanov.decompose.ComponentContext
import com.arkivanov.decompose.router.children.SimpleNavigation
import com.arkivanov.decompose.router.stack.ChildStack
import com.arkivanov.decompose.router.stack.StackNavigation
import com.arkivanov.decompose.router.stack.childStack
import com.arkivanov.decompose.router.stack.childStackWebNavigation
import com.arkivanov.decompose.router.stack.pop
import com.arkivanov.decompose.router.stack.popWhile
import com.arkivanov.decompose.router.stack.pushNew
import com.arkivanov.decompose.router.webhistory.WebNavigation
import com.arkivanov.decompose.value.Value
import com.arkivanov.essenty.lifecycle.coroutines.coroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.channels.BufferOverflow
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable
import one.healthium.base.launcher.PlatformLauncher
import one.healthium.content.navigation.content.DefaultContentComponent
import one.healthium.content.navigation.media.DefaultVideoPlayerComponent
import one.healthium.content.navigation.shopping_list.DefaultShoppingListComponent
import one.healthium.decompose.ModalStack
import one.healthium.decompose.ModalStackNavEvent
import one.healthium.decompose.ModalT
import one.healthium.decompose.dismiss
import one.healthium.decompose.modalStack
import one.healthium.decompose.pop
import one.healthium.decompose.push
import one.healthium.decompose.waitForChild
import one.healthium.health_log.navigation.DefaultHealthLogComponent
import one.healthium.insights.domain.content.ContentOptions
import one.healthium.insights.domain.models.ActionModel
import one.healthium.insights.domain.models.ShoppingListModel
import one.healthium.log_food.ui.camera.DefaultFoodCameraComponent
import one.healthium.log_food.ui.navigation.DefaultLogFoodComponent
import one.healthium.log_food.ui.scanner.navigation.DefaultBarcodeScannerComponent
import one.healthium.meal_plans.tutorial.navigation.DefaultPersonalMealPlanTutorialComponent
import one.healthium.pcos.presentation.navigation.home.DefaultHomeComponent
import one.healthium.pcos.presentation.navigation.log_period.DefaultLogPeriodComponent
import one.healthium.pcos.presentation.navigation.root.PaywallController
import one.healthium.player.ModalVideoPlayer
import one.healthium.profile.navigation.log_water.DefaultLogWaterComponent
import one.healthium.profile.navigation.log_weight.DefaultLogWeightComponent
import one.healthium.profile.navigation.update_age.DefaultUpdateAgeComponent
import one.healthium.profile.navigation.update_food.DefaultUpdateFoodComponent
import one.healthium.profile.navigation.update_height.DefaultUpdateHeightComponent
import one.healthium.profile.navigation.update_initial_weight.DefaultUpdateInitialWeightComponent
import one.healthium.profile.navigation.update_name.DefaultUpdateNameComponent
import one.healthium.profile.navigation.update_weight.DefaultUpdateWeightComponent
import one.healthium.profile.ui.food.models.FoodUiModel
import one.healthium.profile.ui.food_history.models.FoodItemUiModel
import one.healthium.tutorial.domain.TutorialInteractor
import one.healthium.tutorial.domain.TutorialModel
import one.healthium.tutorial.navigation.DefaultTutorialComponent
import one.healthium.update_food_preferences.navigation.DefaultUpdateFoodPreferencesComponent
import one.primehealth.app.DeepLink
import one.primehealth.app.TargetPlatform
import one.primehealth.app.targetPlatform
import org.koin.core.component.KoinComponent
import org.koin.core.component.inject
import kotlin.time.Instant

class DefaultMainComponent(
    componentContext: ComponentContext,
    private val paywallController: PaywallController,
    private val platformLauncher: PlatformLauncher,
) : MainComponent, ComponentContext by componentContext, KoinComponent {

    private val scope = coroutineScope(Dispatchers.Main.immediate + SupervisorJob())
    private val tutorialInteractor by inject<TutorialInteractor>()
    private val barcodeChannel = Channel<String>(capacity = 0, onBufferOverflow = BufferOverflow.SUSPEND)
    private val foodCameraChannel = Channel<List<FoodUiModel>?>(capacity = 0, onBufferOverflow = BufferOverflow.SUSPEND)

    private val nav = StackNavigation<Config>()

    private val _stack = childStack(
        source = nav,
        serializer = Config.serializer(),
        initialStack = { listOf(Config.Home) },
        childFactory = ::child,
        handleBackButton = true,
    )

    private val navModal = SimpleNavigation<ModalStackNavEvent<SlotConfig>>()

    private val _stackModal = modalStack(
        source = navModal,
        serializer = SlotConfig.serializer(),
        initialStack = { listOf() },
        childFactory = ::slotChild,
    )

    init {
        scope.launch {
            paywallController.isActive.first { active -> active.not() }
            delay(1000)
            try {
                val tutorialModel = tutorialInteractor.getTutorial()
                if (tutorialModel != null) {
                    onShowTutorial(tutorialModel)
                }
            } catch (e: Exception) {
                Logger.e("error fetch tutorial", e)
            }
        }
    }

    override val stack: Value<ChildStack<*, MainComponent.Child>> = _stack

    override val stackModal: Value<ModalStack<*, ModalT<MainComponent.SlotChild>>> = _stackModal

    override val webNavigation: WebNavigation<*> =
        childStackWebNavigation(
            navigator = nav,
            stack = _stack,
            serializer = Config.serializer(),
            childSelector = {
                when (val child = it.instance) {
                    is MainComponent.Child.Content -> null
                    is MainComponent.Child.Home -> child.component
                    is MainComponent.Child.ShoppingList -> null
                    is MainComponent.Child.Video -> null
                }
            }
        )

    override fun onBackClicked() {
        nav.pop()
    }

    override fun onDismissModal(config: Any) {
        navModal.pop(config)
    }

    override fun onDeepLink(deepLink: DeepLink) {
        when (deepLink) {
            is DeepLink.Insight -> {
                closeAllModals()
                nav.pushNew(Config.Content(ContentOptions.Insight(deepLink.id)))
            }

            is DeepLink.MealPlan -> {
                closeAllModals()
                nav.pushNew(Config.Content(ContentOptions.MealPlan(deepLink.id)))
            }

            is DeepLink.Recipe -> {
                closeAllModals()
                nav.pushNew(Config.Content(ContentOptions.Recipe(deepLink.id)))
            }

            is DeepLink.Workout -> {
                closeAllModals()
                nav.pushNew(Config.Content(ContentOptions.Workout(deepLink.id)))
            }

            is DeepLink.Paywall -> Unit // handle on root level
            is DeepLink.SilentLogin -> Unit // handle on root level
            is DeepLink.MagicLink -> Unit // handle on root level
            DeepLink.DailyTasks,
            DeepLink.Insights,
            DeepLink.Today -> {
                // TODO fix animation
                nav.popWhile { it != Config.Home }
                closeAllModals()
                stack.waitForChild<MainComponent.Child.Home> { child ->
                    child.component.onDeepLink(deepLink)
                }
            }
        }
    }

    private fun closeAllModals() {
        navModal.navigate { emptyList() }
    }

    override fun onShowTutorial(model: TutorialModel) {
        when (model) {
            is TutorialModel.IntroTutorial -> navModal.push(SlotConfig.IntroTutorial(model))
            is TutorialModel.MealPlanTutorial -> navModal.push(SlotConfig.PersonalMealPlanTutorial)
        }
    }

    private fun child(config: Config, componentContext: ComponentContext): MainComponent.Child =
        when (config) {
            Config.Home -> MainComponent.Child.Home(
                DefaultHomeComponent(
                    componentContext = componentContext,
                    onInsightClicked = ::onInsightClicked,
                    onLogFoodClicked = ::onLogFoodClicked,
                    onMealPlanClicked = ::onMealPlanClicked,
                    onLogWaterClicked = ::onLogWaterClicked,
                    onLogWeightClicked = ::onLogWeightClicked,
                    onWeightHistoryClicked = ::onWeightHistoryClicked,
                    onWorkoutClicked = ::onWorkoutClicked,
                    onFoodHistoryItemClicked = ::onFoodHistoryItemClicked,
                    onActionClicked = ::onActionClicked,
                    onLogPeriodClicked = ::onLogPeriodClicked,
                    onLogSymptomsClicked = ::onLogSymptomsClicked,
                    onUpdateNameClicked = ::onUpdateNameClicked,
                    onUpdateAgeClicked = ::onUpdateAgeClicked,
                    onUpdateHeightClicked = ::onUpdateHeightClicked,
                    onUpdateInitialWeightClicked = ::onUpdateInitialWeightClicked,
                    onUpdateTargetWeightClicked = ::onUpdateTargetWeightClicked,
                )
            )

            is Config.Content -> MainComponent.Child.Content(
                DefaultContentComponent(
                    componentContext = componentContext,
                    options = config.options,
                    onBackClicked = ::onBackClicked,
                    onActionClicked = ::onActionClicked,
                )
            )

            is Config.Video -> MainComponent.Child.Video(
                DefaultVideoPlayerComponent(
                    componentContext = componentContext,
                    url = config.url,
                    onBackClicked = ::onBackClicked,
                )
            )

            is Config.ShoppingList -> MainComponent.Child.ShoppingList(
                DefaultShoppingListComponent(
                    componentContext = componentContext,
                    shoppingList = config.shoppingList,
                    onBackClicked = ::onBackClicked,
                )
            )
        }

    private fun slotChild(config: SlotConfig, componentContext: ComponentContext): MainComponent.SlotChild {
        return when (config) {
            is SlotConfig.LogFood -> MainComponent.SlotChild.LogFood(
                DefaultLogFoodComponent(
                    componentContext = componentContext,
                    taskId = config.taskId,
                    food = config.food,
                    onCloseClicked = {
                        navModal.dismiss { it is SlotConfig.LogFood }
                    },
                    onScanBarcode = {
                        navModal.push(SlotConfig.BarcodeScanner)
                        barcodeChannel.receive()
                    },
                    onScanFood = {
                        navModal.push(SlotConfig.FoodCamera)
                        foodCameraChannel.receive()
                    },
                )
            )

            is SlotConfig.LogWater -> MainComponent.SlotChild.LogWater(
                DefaultLogWaterComponent(
                    componentContext = componentContext,
                    taskId = config.taskId,
                    onCloseClicked = { navModal.dismiss { it is SlotConfig.LogWater } },
                )
            )

            is SlotConfig.LogWeight -> MainComponent.SlotChild.LogWeight(
                DefaultLogWeightComponent(
                    componentContext = componentContext,
                    taskId = config.taskId,
                    onCloseClicked = { navModal.dismiss { it is SlotConfig.LogWeight } },
                )
            )

            is SlotConfig.UpdateWeightHistory -> MainComponent.SlotChild.UpdateWeight(
                DefaultUpdateWeightComponent(
                    componentContext = componentContext,
                    weight = config.weight,
                    date = config.date,
                    onWeightUpdated = { navModal.dismiss { it is SlotConfig.UpdateWeightHistory } },
                    onCloseClicked = { navModal.dismiss { it is SlotConfig.UpdateWeightHistory } },
                )
            )

            is SlotConfig.UpdateFoodHistory -> MainComponent.SlotChild.UpdateFood(
                DefaultUpdateFoodComponent(
                    componentContext = componentContext,
                    food = config.food,
                    onCloseClicked = { navModal.dismiss { it is SlotConfig.UpdateFoodHistory } },
                    onFoodSaved = { navModal.dismiss { it is SlotConfig.UpdateFoodHistory } },
                )
            )

            is SlotConfig.LogPeriod -> MainComponent.SlotChild.LogPeriod(
                DefaultLogPeriodComponent(
                    componentContext = componentContext,
                    taskId = config.taskId,
                    onCloseClicked = { navModal.dismiss { it is SlotConfig.LogPeriod } },
                    onPeriodUpdated = { navModal.dismiss { it is SlotConfig.LogPeriod } },
                )
            )

            is SlotConfig.LogSymptoms -> MainComponent.SlotChild.LogSymptoms(
                DefaultHealthLogComponent(
                    componentContext = componentContext,
                    taskId = config.taskId,
                    onCloseClicked = { navModal.dismiss { it is SlotConfig.LogSymptoms } },
                )
            )

            is SlotConfig.IntroTutorial -> MainComponent.SlotChild.Tutorial(
                DefaultTutorialComponent(
                    componentContext = componentContext,
                    model = config.model,
                    onFinished = { navModal.dismiss { it is SlotConfig.IntroTutorial } },
                )
            )

            SlotConfig.UpdateName -> MainComponent.SlotChild.UpdateName(
                DefaultUpdateNameComponent(
                    componentContext = componentContext,
                    onCloseClicked = { navModal.dismiss { it is SlotConfig.UpdateName } },
                    onNameUpdated = { navModal.dismiss { it is SlotConfig.UpdateName } },
                )
            )

            SlotConfig.UpdateAge -> MainComponent.SlotChild.UpdateAge(
                DefaultUpdateAgeComponent(
                    componentContext = componentContext,
                    onCloseClicked = { navModal.dismiss { it is SlotConfig.UpdateAge } },
                    onAgeUpdated = { navModal.dismiss { it is SlotConfig.UpdateAge } },
                )
            )

            SlotConfig.UpdateHeight -> MainComponent.SlotChild.UpdateHeight(
                DefaultUpdateHeightComponent(
                    componentContext = componentContext,
                    onCloseClicked = { navModal.dismiss { it is SlotConfig.UpdateHeight } },
                    onHeightUpdated = { navModal.dismiss { it is SlotConfig.UpdateHeight } },
                )
            )

            is SlotConfig.UpdateInitialWeight -> MainComponent.SlotChild.UpdateInitialWeight(
                DefaultUpdateInitialWeightComponent(
                    componentContext = componentContext,
                    isTargetWeight = config.isTargetWeight,
                    onCloseClicked = { navModal.dismiss { it is SlotConfig.UpdateInitialWeight } },
                    onWeightUpdated = { navModal.dismiss { it is SlotConfig.UpdateInitialWeight } },
                )
            )

            SlotConfig.BarcodeScanner -> MainComponent.SlotChild.BarcodeScanner(
                DefaultBarcodeScannerComponent(
                    componentContext = componentContext,
                    onBarcodeScanned = { barcode ->
                        scope.launch {
                            try {
                                barcodeChannel.send(barcode)
                                navModal.dismiss { it is SlotConfig.BarcodeScanner }
                            } catch (e: Exception) {
                                Logger.e("error sending barcode", e)
                            }
                        }
                    },
                    onBackClicked = {
                        navModal.dismiss { it is SlotConfig.BarcodeScanner }
                    },
                )
            )

            is SlotConfig.UpdateFoodPreferences -> MainComponent.SlotChild.UpdateFoodPreferences(
                DefaultUpdateFoodPreferencesComponent(
                    componentContext = componentContext,
                    onCloseClicked = { navModal.dismiss { it is SlotConfig.UpdateFoodPreferences } },
                )
            )

            SlotConfig.PersonalMealPlanTutorial -> MainComponent.SlotChild.PersonalMealPlanTutorial(
                DefaultPersonalMealPlanTutorialComponent(
                    componentContext = componentContext,
                    onMealPlanClicked = { id ->
                        navModal.dismiss { it is SlotConfig.PersonalMealPlanTutorial }
                        onMealPlanClicked(id)
                    },
                    onCloseClicked = { navModal.dismiss { it is SlotConfig.PersonalMealPlanTutorial } },
                )
            )

            SlotConfig.FoodCamera -> MainComponent.SlotChild.FoodCamera(
                DefaultFoodCameraComponent(
                    componentContext = componentContext,
                    onFoodScanned = { foods ->
                        scope.launch {
                            try {
                                foodCameraChannel.send(foods)
                                navModal.dismiss { it is SlotConfig.FoodCamera }
                            } catch (e: Exception) {
                                Logger.e("error sending scanned food", e)
                            }
                        }
                    },
                    onBackClicked = {
                        scope.launch {
                            try {
                                foodCameraChannel.send(null)
                                navModal.dismiss { it is SlotConfig.FoodCamera }
                            } catch (e: Exception) {
                                Logger.e("error dismissing food camera", e)
                            }
                        }
                    },
                )
            )
        }
    }

    private fun onInsightClicked(id: String) {
        nav.pushNew(Config.Content(ContentOptions.Insight(id)))
    }

    private fun onLogFoodClicked(taskId: String?) {
        navModal.push(SlotConfig.LogFood(taskId))
    }

    private fun onMealPlanClicked(id: String) {
        nav.pushNew(Config.Content(ContentOptions.MealPlan(id)))
    }

    private fun onLogWaterClicked(taskId: String?) {
        navModal.push(SlotConfig.LogWater(taskId))
    }

    private fun onLogWeightClicked(taskId: String?) {
        navModal.push(SlotConfig.LogWeight(taskId))
    }

    private fun onWeightHistoryClicked(weight: Float, date: Instant) {
        navModal.push(SlotConfig.UpdateWeightHistory(weight, date))
    }

    private fun onWorkoutClicked(workoutId: String) {
        nav.pushNew(Config.Content(ContentOptions.Workout(workoutId)))
    }

    private fun onFoodHistoryItemClicked(item: FoodItemUiModel) {
        navModal.push(SlotConfig.UpdateFoodHistory(item))
    }

    private fun onUpdateNameClicked() {
        navModal.push(SlotConfig.UpdateName)
    }

    private fun onUpdateAgeClicked() {
        navModal.push(SlotConfig.UpdateAge)
    }

    private fun onUpdateHeightClicked() {
        navModal.push(SlotConfig.UpdateHeight)
    }

    private fun onUpdateInitialWeightClicked() {
        navModal.push(SlotConfig.UpdateInitialWeight(false))
    }

    private fun onUpdateTargetWeightClicked() {
        navModal.push(SlotConfig.UpdateInitialWeight(true))
    }

    private fun onActionClicked(action: ActionModel) {
        when (action) {
            is ActionModel.Insight -> nav.pushNew(Config.Content(ContentOptions.Insight(action.id)))
            is ActionModel.Recipe -> nav.pushNew(Config.Content(ContentOptions.Recipe(action.id)))
            is ActionModel.Category -> nav.pushNew(Config.Content(ContentOptions.Category(action.id)))
            is ActionModel.Video -> onVideoClicked(action)
            is ActionModel.Workout -> nav.pushNew(Config.Content(ContentOptions.Workout(action.id)))
            is ActionModel.ShoppingList -> nav.pushNew(Config.ShoppingList(action.shoppingList))
            is ActionModel.MealPlan -> nav.pushNew(Config.Content(ContentOptions.MealPlan(action.id)))
            is ActionModel.LogMeal -> navModal.push(SlotConfig.LogFood(food = FoodUiModel.fromMealNutrition(action.nutrition)))
            is ActionModel.Anchor -> Unit
            ActionModel.UpdateFoodPreferences -> navModal.push(SlotConfig.UpdateFoodPreferences)
            is ActionModel.Favorite -> Unit
            ActionModel.Share -> Unit
        }
    }

    private fun onVideoClicked(action: ActionModel.Video) {
        when (targetPlatform()) {
            TargetPlatform.Android -> nav.pushNew(Config.Video(action.url))
            else -> ModalVideoPlayer(
                url = action.url,
            ).present(platformLauncher)
        }
    }

    private fun onLogPeriodClicked() {
        navModal.push(SlotConfig.LogPeriod())
    }

    private fun onLogSymptomsClicked(taskId: String?) {
        navModal.push(SlotConfig.LogSymptoms(taskId))
    }

    @Serializable
    private sealed interface Config {
        @Serializable
        data object Home : Config

        @Serializable
        data class Content(
            val options: ContentOptions,
        ) : Config

        @Serializable
        data class Video(
            val url: String
        ) : Config

        @Serializable
        data class ShoppingList(
            val shoppingList: ShoppingListModel
        ) : Config
    }

    @Serializable
    private sealed interface SlotConfig {
        @Serializable
        data class LogFood(
            val taskId: String? = null,
            val food: FoodUiModel? = null,
        ) : SlotConfig

        @Serializable
        data class LogWater(
            val taskId: String? = null,
        ) : SlotConfig

        @Serializable
        data class LogWeight(
            val taskId: String? = null,
        ) : SlotConfig

        @Serializable
        data class UpdateWeightHistory(
            val weight: Float,
            val date: Instant,
        ) : SlotConfig

        @Serializable
        data class UpdateFoodHistory(
            val food: FoodItemUiModel,
        ) : SlotConfig

        @Serializable
        data class LogPeriod(
            val taskId: String? = null,
        ) : SlotConfig

        @Serializable
        data class LogSymptoms(
            val taskId: String? = null,
        ) : SlotConfig

        @Serializable
        data class IntroTutorial(
            val model: TutorialModel.IntroTutorial,
        ) : SlotConfig

        @Serializable
        data object UpdateName : SlotConfig

        @Serializable
        data object UpdateAge : SlotConfig

        @Serializable
        data object UpdateHeight : SlotConfig

        @Serializable
        data class UpdateInitialWeight(val isTargetWeight: Boolean) : SlotConfig

        @Serializable
        data object BarcodeScanner : SlotConfig

        @Serializable
        data object UpdateFoodPreferences : SlotConfig

        @Serializable
        data object PersonalMealPlanTutorial : SlotConfig

        @Serializable
        data object FoodCamera : SlotConfig
    }
}